import React from 'react';
import BaseView from '../../components/CRUDView';
import Utils, {exportFilteredCSV} from '../../common/Utils';
import {TractorOwnerEndpoints, TractorsEndpoints} from '../../common/ProxyEndpoints';
import Avatar from '../../components/ProfileAvatar';
import EntityImageDisplay from "../../components/EntityImageDisplay/EntityImageDisplay";
import AppVariables from "../../common/AppVariables";
import DialogCollapseSection from "../../components/DialogCollapseSection/DialogCollapseSection";
import {Dialogs, Permissions, TractorState} from "../../common/Constants";
import TractorUtils from "./TractorUtils";
import HistoryLink from "../../common/HistoryLink";
import {Badge} from 'reactstrap';
import OperationSummaryComponent, {FixedValueDisplay} from '../../components/OperationSummaryComponent/OperationSummaryComponent';
import './Tractors.css';
import UncontrolledAlert from "reactstrap/es/UncontrolledAlert";
import CreateTractorDialog from "../../components/CreateTractorDialog/CreateTractorDialog";
import TractorRequestsView from "./TractorRequestsView";
import {
    ConditionMap,
    CustomTractorsFilterView,
    parseTractorQueryFilters,
    TRACTORS_QUERY_FILTERS
} from "./TractorsCommon";
import {ExpanderView, SectionedList} from "../../components/AuditExplorer/Elements";
import AssignmentRulesConfigDialog, {AssignmentRuleInfo} from "./AssignmentRulesConfigDialog";
import NumberLabel from "../../components/NumberLabel/NumberLabel";
import WorkloadComponent from "./WorkloadComponent";
import {broadcastEvent} from "../../common/Events/EventSource";
import EventActions from "../../common/Events/Actions";

const SearchProps  = [
    'name',
    'chassis',
    'tractor_model',
    'registration_number',
    'year',
    'owner.name',
    'operator.name'
];

const CacheDependency = [
    'dashboard',
    'tractor_owners',
    'operators'
];


/**
 * Handles management of tractors by administrators
 */
class TractorsAdmin extends BaseView {

    constructor(props){
        super(props,'tractors_admin');
    }

    fetchOperatorsForOwner(ownerId,idleOnly,exclude,include){

        //  already loaded
        if(ownerId == this.state.sourceOwner){
            return;
        }

        const me = this;

        this.setState({
            sourceOwner: ownerId,
            isBusy: true
        });

        return TractorUtils.fetchOwnerOperators(ownerId,idleOnly,exclude,include).then(operators =>{

            me.setState({
                operators: operators
            });

        }).catch(err => {

            //  show error and allow retry option


        }).then(() => {

            me.setState({
                isBusy: false
            });

        });

    }

    beginShowCreate() {
        this._createTractorDlg.show();
    }

    renderDialogs(dialog) {

        return <React.Fragment>
            {super.renderDialogs(dialog)}
            <CreateTractorDialog ref={r => this._createTractorDlg = r}
                                 includeOwner
                                 onExit={tractor => this.refresh()}/>
            <TractorRequestsView ref={r => this._requestsView = r}/>
        </React.Fragment>

    }

    beforeCloseDialog(dialog){

        if(dialog == Dialogs.CreateOrEdit){
            this.setState({
                sourceOwner: null,
                operators: null
            });
        }

    }

    beginShowDetails(tractor){

        //  relay to base view
        super.beginShowDetails(tractor);

        //
        this.setStateSafe({
            statsTimeRange: "overall" ,
            startQueryDate: null ,
            endQueryDate: null
        });

        //  fetch requests assigned to operator
        this.fetchRequests(tractor, "overall" );
    }

    fetchRequests(tractor, timeFrame, offset ,customRange){

        const me = this;

        this.setState({
            isBusy: true
        });

        //  get requests for tractor
        return this.proxy.processRequest(TractorsEndpoints.getOperationSummary(tractor.id,timeFrame, offset ,customRange)).then(response => {

            tractor.operationSummary = response.getPayload();

            me.setState({
                summaryError: null,
                detailItem: tractor,
                isBusy: false
            });

        }).catch(err => {

            me.setState({
                isBusy: false,
                summaryError: err
            });

        });
    }

    fetchTractorOwners(){

        return this.execReq(TractorOwnerEndpoints.getUrl(),{
            fields: ['id','name'],
            include: [ 'user', 'media' ],
            method: 'get'
        });

    }

    getCustomFilterState(filters) {
        return parseTractorQueryFilters(filters);
    }

    /**
     * Initialize view
     */
    componentWillMount(){

        const me = this;

        this.setViewOptions({

            //  The message to show when there are no items in the view
            noRowsMessage: 'No tractors available yet!',

            //  The class icon for the card header
            pageIcon: 'tractor_white',

            //  The title for the content frame
            pageTitle: 'Tractors',

            //  The total number of items per page
            maxItemsPerPage: AppVariables.MaxItemsPerPage,

            //  Defines the features of the view
            features: [ 'toolbox' , 'search' ],

            multiSelection: {

                //  enable multi-selection
                enable : true,
            },

            model: {
                include: [ 'operator' , 'owner' , 'media' , 'assignment_rules' ]
            },

            hooks: {

                preQueryHook:() => {

                    return me.fetchTractorOwners().then(response =>{
                        return {
                            owners: response.getPayload()
                        };
                    });

                },

                postProcessorHook: (action,response) => {
                    Utils.eraseSession(CacheDependency);
                },

                renderCellActions: (item) => [
                    {
                        //  Show tractor requests
                        label: "View Requests" , icon: 'fa fa-eye' , onClick: () => {
                            return Utils.navigateLink(`/requests?tractor_id=${encodeURIComponent(item.id.toString())}&_tractor_select_name=${encodeURIComponent(item.name)}`)
                        }
                    }
                ]

            },

            //  defines (CRUD) actions enabled on the items
            actions: {

                //  create action options
                create: {
                    enabled: true,
                    text: 'Add Tractor',
                    successCreateMsg: 'Tractor added successfully!',
                    failCreateMsg : 'Failed adding tractor!',
                    editTitleMsg: 'Edit Tractor',
                    createTitleMsg: 'Add Tractor',
                    permissions:  [ Permissions.Tractors.Create ]
                },

                //  delete action options
                delete:{

                    enabled : false,

                    //  Disallows the deleting of many items ??
                    disallowMany: false,

                    successDeleteMsg: 'Deleted tractor successfully!',
                    failDeleteMsg: 'Failed deleting tractor!',
                    text: 'Delete Tractor',
                    confirmTitle: 'Confirm Delete Tractor',
                    confirmMessage: 'Are you sure you want to delete tractor?',

                    //  The message to show when deleting many items
                    confirmManyMessage: 'Delete selected tractors?',
                },

                //  delete action options
                edit:{
                    enabled : true,
                    successEditMsg: 'Tractor edited successfully!',
                    failEditMsg: 'Failed editing tractor',
                    text: 'Edit',
                    permissions:  [ Permissions.Tractors.Update ]
                }

            },

            queryActions: {
                endpoint: TractorsEndpoints.getUrl()
            },

            filters: TRACTORS_QUERY_FILTERS,

            renderCustomFilters: (props) => <CustomTractorsFilterView includeOwner {...props}/>,

            search: {

                placeholder: 'Search tractors...',

                //  the mode for searching
                //  immediate: responsive
                //  explicit: wait till user hits the search button
                mode: 'explicit',

                //  The minimum characters to begin search with
                minSearchChars: 2,

                properties: SearchProps

            },

            sort: {

                properties: [
                    {
                        label: "Date Added",
                        value: "date_created"
                    },
                    {
                        label: "Name",
                        value: "name"
                    },
                    {
                        label: "Registration Number",
                        value: "registration_number"
                    }
                ]
            },

            //  Defines actions for toolbox
            toolBox: [
                {  text : 'Add Tractor'  , icon : 'fa fa-plus'  , invoke: () => me.beginShowCreate() , position: 'right' , permissions:  [ Permissions.Tractors.Create ] },
                {  text : 'Refresh'  , icon : 'fa fa-refresh'  ,
                    invoke: () => {

                        //  refresh view
                        me.refresh();

                        //  broadcast event for child components
                        broadcastEvent(EventActions.RefreshActivePage);

                    } ,
                    position: 'right'
                },
                {  text : 'Condition'  , icon : 'fa fa-bars' , invoke: (index) => me.updateQuery('state',ConditionMap[index].value) , position: 'right' , group : 'filter' , items: ConditionMap.map(x => x.label) },
                {  text : null  , icon : 'fa fa-sort' , invoke: (index) => { me.configureSort();  }, position: 'right' , group : 'filter'},
                {  text : ''  , icon : 'fa fa-gears' , invoke: (index) => {

                    switch (index){
                        case 0:
                            return exportFilteredCSV(TractorsEndpoints.exportCSV(), { component: me });
                        case 1:
                            return  me.toggleCustomFilterVisibility();
                    }

                }, position: 'right' , group : 'more' ,
                    items: () => ([
                        <span><i className="fa fa-file-text"/> Export to CSV</span>,
                        <span><i className={me.isCustomFilterViewVisible() ? "fa fa-chevron-up" : "fa fa-chevron-down"}/> {me.isCustomFilterViewVisible() ? "Hide" : "Show"} Advanced Filters</span>
                    ]),
                    sticky: false,
                }
            ],

            //  Inputs used in editing and creating items
            inputs: [
                {
                    render: (context) => {

                        const owners = me.state.owners;
                        if(owners && owners.length === 0){
                            return <UncontrolledAlert color={"warning"}><i className="fa fa-info-circle text-gray-dark"/> There are no registered tractor owners. You need to register tractor owners before tractors!</UncontrolledAlert>
                        }

                        return null;
                    }
                },
                { name:  'owner' , title: 'Tractor Owner' , type: 'select' , format: 'number' , icon: 'fa fa-user' , required: { value: true, message: "Please select the tractor owner the tractor belongs to" },

                    setValue: (input,tractor) => {

                        const formRenderer = me.getDialogRenderer(Dialogs.CreateOrEdit);
                        let value = tractor.owner ? tractor.owner.id : null;
                        if(value){

                            //
                            input.value = value;

                            //  get operator tractors
                            me.fetchOperatorsForOwner(value,true , undefined , tractor.operator ? [tractor.operator.id] : undefined).then(() =>{

                                //  refresh tractor
                                formRenderer.refreshInput('operator',tractor);

                            });
                        }

                    },

                    refresh: () => {

                        return me.fetchTractorOwners().then(response =>{

                            me.setState({
                                owners: response.getPayload()
                            });

                        });
                    },

                    //  return items
                    items: () => me.state.owners.map((o) => {
                        return { value: o.id , text: o.name }
                    }),

                    //  on changed
                    onChange: (id) => {

                        const formRenderer = me.getDialogRenderer(Dialogs.CreateOrEdit);
                        const tractor =  me.state.editItem;

                        //
                        me.fetchOperatorsForOwner(id,true,undefined,tractor && tractor.operator ? [tractor.operator.id] : undefined );
                        formRenderer.removeInputError('operator');
                    }
                },
                { render: () => <hr/> },
                {name: 'name', title: 'Name' , hint: 'Enter tractor name' , type: 'text' , required : true },
                {
                    group:[
                        {name: 'tractor_model', title:'Model' , hint: 'Enter tractor model' , type: 'text' , required: true , icon: 'tractor' },
                        {name: 'registration_number', title:'Registration Number' , type: 'text' , hint: 'Enter registration number here...' , required: true },
                        {name: 'chassis', title: 'Chassis'  , hint: 'Enter tractor chassis number'  ,type: 'text' , required: true },
                        {name: 'year', title:'Year', type: 'select' , format: 'number' , setValue: (input,item) => input.value =  item.year, items: () => Utils.GenerateRange(1990, Number(AppVariables.ApplicationYear)).map((item) => { return { value: item , text: item } }) , required: true },
                    ],
                    render: (content,key) => {
                        return <DialogCollapseSection initiallyOpen={true} title={"Description"} key={key}>
                            {content}
                        </DialogCollapseSection>
                    }
                },
                { name: 'state' , title: 'Tractor Status' , type: 'select' , format: 'number' , comment: <p>Indicates whether you tractor is available to be assigned to requests</p> , items:
                        [
                            { text : "Active" , value: TractorState.Active },
                            { text : "Inactive (Not Assignable)" , value: TractorState.Faulted },
                        ]
                },
                {
                    group: [
                        {
                            name: 'operator', title: 'Operator' , type: 'select' , format: 'number' , icon: 'fa fa-user',

                            comment: <p>Assign an operator to the tractor by selecting it below. A tractor can be created without assigning any operator</p>,

                            setValue: (input,item) => {
                                input.value = item.operator ? item.operator.id : '';
                            },

                            refresh: () => {
                                me.getDialogRenderer(Dialogs.CreateOrEdit).findInput('owner').refresh();
                            },

                            items: () => {
                                const operators = me.state.operators || [];
                                return Utils.insertNone(operators.map((item) => { return { value : item.id , text :  item.name } }));
                            }
                        }
                    ],

                    render: (content,key) => {
                        return (<div key={key}>
                            <hr/>
                            <DialogCollapseSection  title={"Optional"} onExpand={() => {

                                const formRenderer = me.getDialogRenderer(Dialogs.CreateOrEdit);
                                const id = (formRenderer.getInputValueWithName("owner") || me.state.owners[0].id);
                                if(id !== undefined){
                                    const owner = me.state.owners.find(x => x.id == id);
                                    me.fetchOperatorsForOwner(id,true);
                                }

                            }}>
                                {content}
                            </DialogCollapseSection>
                        </div>)
                    }
                },


            ],

            // Describes the columns present in the table
            columns : [
                {key: '__img' , label: ' ' , cell: (item,k) => <Avatar media={item.media} name={Utils.formatAvatarName(item.name)} size={32} /> , mobile: true },
                {key: 'name', label: () => me.sortCol('Name','name') , mobile: true },
                {key: 'registration_number', label: () => me.sortCol('Reg. No','registration_number') , mobile: true },
                {key: 'owner', label: 'Owner' , format: (owner) => owner ? owner.name : null },
                {key: 'operator', label: 'Operator' , format: (operator) => operator ? operator.name : <h6><Badge pill={true}>Unassigned</Badge></h6>},
                {key: 'workload', label: 'Workload' , cell: item => <WorkloadComponent tractor={item}/> },
            ],

            display: {
                rowClass: tractor => tractor.state == TractorState.Faulted ? 'inactive-tractor-row' : ''
            },

            //  Describes details to show when clicked
            detailsDisplay: {

                //  enable or disable
                enabled: true,

                renderTitle: () => {
                    return "Tractor Info";
                },

                renderActions: (item,  { toggle }) => ([
                    {
                        label: "View Requests" , icon: "fa fa-eye", color: "success", onClick: () => {
                            toggle();
                            return Utils.navigateLink(`/requests?tractor_id=${encodeURIComponent(item.id.toString())}&_tractor_select_name=${encodeURIComponent(item.name)}`)
                        }
                    }
                ]),

                customRender: (item) => {

                    const hasImage = item.media.count > 0;
                    const me = this;

                    return <div>
                        <div className="text-center">
                            <h5>Attached Image</h5>
                            <EntityImageDisplay
                                entityName="tractor"
                                defaultSrc={"/img/avatars/tractor_utility.jpg"}
                                entityId={item.id}
                                crudView={me}
                                onRemove={(media) => {
                                    me.updateItemMedia(item,media);
                                }}
                                onCompleteUpload={(media) => {
                                    me.updateItemMedia(item,media);
                                    return false;
                                }}
                                media={item.media} />

                        </div>

                        <DialogCollapseSection title={"Info"} initiallyOpen={true}>
                            <hr/>
                            <div className="row">
                                <div className="col-md-6">
                                    <dl>
                                        <dt>Name</dt>
                                        <dl>{item.name}</dl>

                                        <dt>Chassis</dt>
                                        <dl>{item.chassis}</dl>

                                        <dt>Model</dt>
                                        <dl>{item.tractor_model}</dl>

                                        <dt>Year</dt>
                                        <dl>{item.year}</dl>

                                        <dt>Operator</dt>
                                        <dl>{item.operator ? <HistoryLink url={`/operators?filter_id=${item.operator.id}&q=${item.operator.name}`} className="btn-link" tooltip='Click to see tractor operator'>{item.operator.name}</HistoryLink> : <h6><Badge pill={true}>Unassigned</Badge></h6>}</dl>

                                    </dl>
                                </div>
                                <div className="col-md-6">
                                    <dl>
                                        <dt>Registration No.</dt>
                                        <dl>{item.registration_number}</dl>

                                        <dt>Owner</dt>
                                        <dl>{item.owner ? <HistoryLink url={`/tractor_owners?filter_id=${item.owner.id}&q=${item.owner.name}`} tooltip="Click to see tractor owner" className="btn-link">{item.owner.name}</HistoryLink> : "N/A" }</dl>

                                        <dt>Current State</dt>
                                        <dl>{TractorUtils.renderState(item) }</dl>

                                        <dt>Date Added</dt>
                                        <dl>{Utils.formatDateStandard(item.date_created)}</dl>
                                    </dl>
                                </div>
                            </div>
                        </DialogCollapseSection>


                        <OperationSummaryComponent
                            operationSummary={item.operationSummary}
                            onQuery={(timeRange,rangeInfo) => me.fetchRequests(item,timeRange,rangeInfo)}
                            renderRevenueStats={summary => (<div>



                                <div className="row">

                                    <div className="col-md-4 text-center">
                                        <h6><Badge pill color={"info"}>Pending <i className="fa fa-chevron-up"/></Badge></h6>
                                        <FixedValueDisplay id="total_pending_income"
                                                           value={summary.totalPendingIncome}
                                                           message={AppVariables.CurrencyCode}
                                                           hint="Indicates revenue to be generated from assigned requests"/>

                                    </div>

                                    <div className="col-md-4 text-center">

                                        <h6><Badge pill color={"success"}>Payout <i className="fa fa-chevron-down"/></Badge></h6>

                                        <FixedValueDisplay id="total_completed_income_payout"
                                                           value={summary.totalIncomePayout}
                                                           message={AppVariables.CurrencyCode}
                                                           hint="Shows total payout for tractor owner"/>

                                    </div>

                                    <div className="col-md-4 text-center">

                                        <h6><Badge pill color={"secondary"}>Raw Revenue <i className="fa fa-chevron-down"/></Badge></h6>

                                        <FixedValueDisplay id="total_completed_income"
                                                           value={summary.totalIncome}
                                                           message={AppVariables.CurrencyCode}
                                                           hint="Shows the raw total revenue generated from completed requests (using raw cost)"/>

                                    </div>
                                </div>

                                <div className="text-center mt-2">


                                        <h6><Badge pill>Commission <i className="fa fa-chevron-down"/></Badge></h6>

                                        <FixedValueDisplay id="total_commission_income"
                                                           value={summary.totalIncomeCommission}
                                                           message={AppVariables.CurrencyCode}
                                                           hint="Shows total commission generated from completed requests with the tractor"/>

                                </div>


                            </div>)}
                            renderOperationStats={summary => (<div>

                                <div className="row">

                                    <div className="col-md-4 text-center">
                                        <h6><Badge pill color={"danger"}>Cancelled <i className="fa fa-close"/></Badge></h6>
                                        <FixedValueDisplay id="total_cancelled_requests"
                                                           value={summary.totalCancelledRequests}
                                                           hint="Shows to total number of requests cancelled within the given time specified"/>
                                    </div>


                                    <div className="col-md-4 text-center">
                                        <h6><Badge pill color={"info"}>Assigned <i className="fa fa-chevron-up"/></Badge></h6>
                                        <FixedValueDisplay id="total_assigned_acres"
                                                           value={summary.totalHectaresAssigned}
                                                           message={summary.totalHectaresAssigned == 1 ? "hectare" : "hectares"}
                                                           hint="Shows total hectare(s) assigned to the tractor over the time range specified"/>
                                    </div>

                                    <div className="col-md-4 text-center">
                                        <h6><Badge pill >Completed <i className="fa fa-chevron-down"/></Badge></h6>
                                        <FixedValueDisplay id="total_completed_acres"
                                                           value={summary.totalHectaresCompleted}
                                                           message={summary.totalHectaresCompleted == 1 ? "hectare" : "hectares"}
                                                           hint="Shows completed hectares within specified time"/>
                                    </div>

                                </div>

                                <hr/>

                                <div className="m-4">

                                    <SectionedList items={[
                                        {
                                            title: "Total Assigned Hour(s)",
                                            value: <NumberLabel value={summary.totalAssignedDuration} maximumFractionDigits={1}/>
                                        },
                                        {
                                            title: "Total Working Hour(s)",
                                            value: <NumberLabel value={summary.totalWorkingDuration} maximumFractionDigits={1}/>
                                        }
                                    ]}/>

                                </div>


                                </div>)}/>

                        <div className="mt-2">

                            <ExpanderView isExpanded={false}
                                          alignContent={false}
                                          title="Assignment Conditions">

                                <AssignmentRulesConfigDialog ref={r => this._assignmentConfigDlg = r}
                                                             afterUpdate={async () => {

                                                                 //  query rules for tractor and update model
                                                                 item = await this.updateItemSingle(item.id);

                                                                 // refresh detail item
                                                                 this.setStateSafe({ detailItem: item });

                                                             }}/>

                                <AssignmentRuleInfo rules={item.assignment_rules}
                                                    onEdit={() => this._assignmentConfigDlg.show(item)}/>

                            </ExpanderView>
                        </div>

                    </div>

                }
            },

            //  Processors
            processors: {

                //  create new item
                createItem: (proxy , payload) => {

                    const ownerId = payload.owner;

                    if(payload.operator){
                        payload.operator_id = payload.operator;
                    }

                    //  remove unwanted properties
                    delete payload.operator;
                    delete payload.owner;

                    //  use proxy to create a new tractor for the selected operator
                    return proxy.processRequest(TractorsEndpoints.create(ownerId,payload));
                },

                //  called to delete many items
                deleteMany: (proxy,items) => {
                    return proxy.processRequest(TractorsEndpoints.deleteTractor(items.map((item) => item.id)));
                },

                //  on edit item
                onEditItem: (proxy,item, payload, affected ) => {

                    //
                    affected.owner_id = affected.owner;
                    affected.operator_id = payload.operator || null;

                    //
                    delete affected.owner;
                    delete affected.operator;

                    if(Object.keys(affected).find(x => affected[x] !== undefined)) {

                        affected.last_updated = item.last_updated;
                        return proxy.processRequest(TractorsEndpoints.update(item.id, affected));
                    }
                    else{

                        return Promise.resolve();
                    }
                },

                //  on delete item
                onDeleteItem: ( proxy,item) => {
                    return proxy.processRequest(TractorsEndpoints.deleteTractor(item.id));
                }

            }
        });
    }

}


export default TractorsAdmin;


// WEBPACK FOOTER //
// ./src/views/Tractors/TractorsAdmin.js